import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { admin as api } from "@/lib/api/admin";
import type { KolInput } from "@/lib/api/admin";
import { ApiError } from "@/lib/api/transport";
import type { KolStats, KolStatus, KolWithStats } from "@/types/api";
import { useAuth } from "./useAuth";

// Distinct mutation-error tag the create/edit modals render specially.
export type KolMutationError = "slug_taken" | "slug_reserved" | "unknown";

export class KolMutationFailed extends Error {
  readonly code: KolMutationError;
  constructor(code: KolMutationError) {
    super(code);
    this.code = code;
  }
}

function tagApiError(err: unknown): never {
  if (err instanceof ApiError) {
    const code =
      typeof err.body === "object" &&
      err.body !== null &&
      "error" in err.body &&
      typeof (err.body as { error?: unknown }).error === "string"
        ? (err.body as { error: string }).error
        : null;
    if (code === "slug_taken") throw new KolMutationFailed("slug_taken");
    if (code === "slug_reserved") throw new KolMutationFailed("slug_reserved");
  }
  throw new KolMutationFailed("unknown");
}

export type AdminKolsStatus = "idle" | "loading" | "ready" | "error";

// Stats now arrive with every Kol row from /admin/kols (computed
// server-side by Admin::KolStats). Re-export the canonical shapes so
// admin components can keep importing them from the hook layer.
export type { KolStats, KolWithStats } from "@/types/api";

export interface AdminKolsFilter {
  status?: KolStatus;
  platform?: string;
}

export interface AdminKolsContextValue {
  status: AdminKolsStatus;
  kols: readonly KolWithStats[];
  error: Error | null;
  reload: (filter?: AdminKolsFilter) => Promise<void>;
  create: (input: KolInput) => Promise<KolWithStats>;
  update: (id: string, patch: KolInput) => Promise<KolWithStats>;
  remove: (id: string) => Promise<void>;
  patchStats: (id: string, stats: KolStats) => void;
}

// Re-export so admin components can pull these types from the hook layer
// rather than reaching into @/lib/api directly (no-restricted-imports).
export type {
  KolInput,
  KolPayoutInput,
  KolActivityInput,
} from "@/lib/api/admin";

const AdminKolsContext = createContext<AdminKolsContextValue>(null!);

export function useAdminKols() {
  return useContext(AdminKolsContext);
}

function useAdminKolsProvider(): AdminKolsContextValue {
  const { user, status: authStatus } = useAuth();
  const [status, setStatus] = useState<AdminKolsStatus>("idle");
  const [items, setItems] = useState<readonly KolWithStats[]>([]);
  const [error, setError] = useState<Error | null>(null);

  const reload = useCallback(
    (filter?: AdminKolsFilter) =>
      api
        .listKols(filter)
        .then(({ kols }) => {
          setItems(kols);
          setStatus("ready");
          setError(null);
        })
        .catch((e: Error) => {
          setError(e);
          setStatus("error");
        }),
    [],
  );

  useEffect(() => {
    if (authStatus !== "authenticated" || !user?.admin) return;
    void reload();
  }, [authStatus, user?.admin, reload]);

  const create = useCallback(async (input: KolInput) => {
    try {
      const { kol } = await api.createKol(input);
      setItems((prev) => [kol, ...prev]);
      return kol;
    } catch (err) {
      tagApiError(err);
    }
  }, []);

  const update = useCallback(async (id: string, patch: KolInput) => {
    try {
      const { kol } = await api.updateKol(id, patch);
      setItems((prev) => prev.map((k) => (k.id === id ? kol : k)));
      return kol;
    } catch (err) {
      tagApiError(err);
    }
  }, []);

  const patchStats = useCallback((id: string, stats: KolStats) => {
    setItems((prev) =>
      prev.map((kol) => (kol.id === id ? { ...kol, stats } : kol)),
    );
  }, []);

  const remove = useCallback(async (id: string) => {
    await api.deleteKol(id);
    setItems((prev) => prev.filter((k) => k.id !== id));
  }, []);

  return useMemo(
    () => ({
      status,
      kols: items,
      error,
      reload,
      create,
      update,
      remove,
      patchStats,
    }),
    [status, items, error, reload, create, update, remove, patchStats],
  );
}

export function AdminKolsProvider({ children }: { children: ReactNode }) {
  const value = useAdminKolsProvider();
  return (
    <AdminKolsContext.Provider value={value}>
      {children}
    </AdminKolsContext.Provider>
  );
}
